import { create } from 'zustand';

export type ErrorType = 'unauthorized' | 'forbidden' | 'not_found' | 'server_error' | null;

interface ApiError {
  type: ErrorType;
  status?: number;
  message?: string;
  errorId?: string;
  timestamp?: string;
}

interface ErrorState {
  error: ApiError | null;
  setError: (error: ApiError | null) => void;
  clearError: () => void;
  setUnauthorized: (message?: string) => void;
  setForbidden: (message?: string) => void;
  setNotFound: (message?: string) => void;
  setServerError: (status?: number, message?: string, errorId?: string) => void;
}

export const useErrorStore = create<ErrorState>((set) => ({
  error: null,

  setError: (error) => set({ error }),

  clearError: () => set({ error: null }),

  setUnauthorized: (message) => set({
    error: {
      type: 'unauthorized',
      status: 401,
      message: message || 'You need to log in to access this resource',
      timestamp: new Date().toISOString(),
    },
  }),

  setForbidden: (message) => set({
    error: {
      type: 'forbidden',
      status: 403,
      message: message || 'You do not have permission to access this resource',
      timestamp: new Date().toISOString(),
    },
  }),

  setNotFound: (message) => set({
    error: {
      type: 'not_found',
      status: 404,
      message: message || 'The requested resource was not found',
      timestamp: new Date().toISOString(),
    },
  }),

  setServerError: (status = 500, message, errorId) => set({
    error: {
      type: 'server_error',
      status,
      message: message || 'An unexpected error occurred',
      errorId,
      timestamp: new Date().toISOString(),
    },
  }),
}));

// Helper hook for checking specific error types
export const useHasError = (type: ErrorType) => {
  const error = useErrorStore((state) => state.error);
  return error?.type === type;
};
